import React, { useEffect } from 'react';

const LockMarker = () => (
  <span
    className="absolute -bottom-1 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white text-secondary-500"
    aria-hidden="true"
  >
    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z"
        clipRule="evenodd"
      />
    </svg>
  </span>
);

const CompleteMarker = () => (
  <span
    className="absolute -bottom-1 -right-0.5 flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white text-success-500"
    aria-hidden="true"
  >
    <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
      <path
        fillRule="evenodd"
        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
        clipRule="evenodd"
      />
    </svg>
  </span>
);

/**
 * Fixed-width icon rail. Hovering or focusing an icon names it in a flyout
 * beside the rail, so the rail never resizes and page content never shifts.
 * On small screens it renders as a drawer instead, driven by `mobileOpen`.
 *
 * The drawer is only mounted while open, so the product walkthrough never
 * measures a hidden duplicate of a `data-tour` target. If both are mounted the
 * desktop rail comes first in the DOM and wins `querySelector`.
 */
const DashboardSidebar = ({
  items,
  activeId,
  onSelect,
  ariaLabel = 'Sections',
  title,
  mobileOpen = false,
  onMobileClose,
  footerItem,
}) => {
  useEffect(() => {
    if (!mobileOpen || !onMobileClose) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onMobileClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen, onMobileClose]);

  const handleSelect = (item) => {
    if (onSelect) onSelect(item);
    if (onMobileClose) onMobileClose();
  };

  const renderItem = (item, { showLabels, withTourAttr }) => {
    const isActive = item.id === activeId;
    const flyoutText = item.locked && item.lockedHint ? item.lockedHint : item.label;

    return (
      <div key={item.id} className="group relative">
        <button
          type="button"
          data-tour={withTourAttr ? item.dataTour : undefined}
          onClick={() => handleSelect(item)}
          className={`sidebar-rail-item ${isActive ? 'sidebar-rail-item-active' : ''} ${item.accent ? 'sidebar-rail-item-accent' : ''}`}
          aria-label={item.label}
          aria-current={isActive ? 'page' : undefined}
        >
          <span className="sidebar-rail-icon">
            {item.icon}
            {item.locked && <LockMarker />}
            {!item.locked && item.complete && <CompleteMarker />}
          </span>
          {showLabels && <span className="sidebar-rail-label">{item.label}</span>}
        </button>

        {/* Collapsed rail: name the hovered icon in place instead of widening the rail. */}
        {!showLabels && <span className="sidebar-rail-flyout">{flyoutText}</span>}
      </div>
    );
  };

  return (
    <>
      {/* Reserves layout width for the fixed rail. */}
      <div className="hidden w-16 shrink-0 lg:block" aria-hidden="true" />

      <aside className="fixed bottom-0 left-0 top-16 z-40 hidden w-16 flex-col overflow-visible border-r border-secondary-200 bg-white lg:flex">
        {/* overflow-visible so the hover flyouts are not clipped at the rail edge. */}
        <nav aria-label={ariaLabel} className="flex-1 overflow-visible px-2 py-3">
          <div className="space-y-1">
            {items.map((item) => renderItem(item, { showLabels: false, withTourAttr: true }))}
          </div>
        </nav>
        {footerItem && (
          <div className="border-t border-secondary-200 px-2 py-3">
            {renderItem(footerItem, { showLabels: false, withTourAttr: true })}
          </div>
        )}
      </aside>

      {mobileOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={onMobileClose} aria-hidden="true" />
          <aside
            className="fixed bottom-0 left-0 top-0 z-50 flex w-72 flex-col border-r border-secondary-200 bg-white lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label={ariaLabel}
          >
            <div className="flex items-center justify-between border-b border-secondary-200 px-4 py-3">
              <span className="text-sm font-semibold text-secondary-900">{title || ariaLabel}</span>
              <button type="button" onClick={onMobileClose} className="btn-ghost" aria-label="Close menu">
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <nav aria-label={ariaLabel} className="flex-1 space-y-1 overflow-y-auto px-2 py-3">
              {items.map((item) => renderItem(item, { showLabels: true, withTourAttr: true }))}
            </nav>
            {footerItem && (
              <div className="border-t border-secondary-200 px-2 py-3">
                {renderItem(footerItem, { showLabels: true, withTourAttr: true })}
              </div>
            )}
          </aside>
        </>
      )}
    </>
  );
};

export default DashboardSidebar;
